import React, { useEffect, useState } from "react";
import { CustomTablePagination, EnhancedSearchTableToolbar } from "../../Utils/Tables/CustomTable";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import * as snackbarActions from "../../../actions/snackbarActions";
import MainSnackbar from "../../Utils/Snackbar/SmsSnackbar";
import { Container } from "../../Utils/styledComponents";
import axios from "axios";
import { config } from "../../../Helpers/env";
import { logoutErrorMessage } from "../../../Helpers/ErrorMessages";
import { LoadingGif } from "../../Utils/Loader";
import { CustomTableCell, MuiPaper, StickyHeadTable } from "../../Utils/Tables/StickyHeaderTable";
import PageTitle from "../../Utils/smsTitle";
import { SubHeader } from "../../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPills, faChartBar } from "@fortawesome/free-solid-svg-icons";
import { useDebounce } from "../../Lab/useDebounce";
import { BackButton, DataExportButton } from "../../Utils/Buttons/DataExportationButton";
import { useExcelReports, usePdfDownload } from "../../Utils/ReusableComponents/useExcelExport";
import { dateConvert } from "../../Utils/ConvertDate";
import { DateTimeFilter } from "../../Utils/ReusableComponents/DateTimeFilter";

// Columns
const headData = [
  { id: "count", label: "Sl" },
  { id: "product_name", label: "Product Name" },
  { id: "generic_name", label: "Generic Name" },
  { id: "country_of_origin", label: "Origin" },
  { id: "jan", label: "Jan" },
  { id: "feb", label: "Feb" },
  { id: "mar", label: "Mar" },
  { id: "apr", label: "Apr" },
  { id: "may", label: "May" },
  { id: "jun", label: "Jun" },
  { id: "jul", label: "Jul" },
  { id: "aug", label: "Aug" },
  { id: "sep", label: "Sep" },
  { id: "oct", label: "Oct" },
  { id: "nov", label: "Nov" },
  { id: "dec", label: "Dec" },
];

// Comma formatter
const formatNumber = (num) => {
  return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0";
};

const ConsumptionReport = ({ actions, snackbars }) => {
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [data, setData] = useState({ reportList: [], loading: "idle", total: 0 });
  const [activePage, setActivePage] = useState(1);
  const [debouncedValue] = useDebounce(searchValue, 600);

  const [date, setDate] = useState({
    start_date: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
    end_date: dateConvert(),
  });

  const { loading, total, reportList } = data;
  const { start_date, end_date } = date;

  useEffect(() => {
    setData({ ...data, loading: "pending" });

    axios
      .get(`${config.smsUrl}/creport/consumption_report`, {
        params: {
          search: debouncedValue,
          page: activePage,
          per_page: rowsPerPage,
          start_date,
          end_date,
        },
      })
      .then((res) => {
        const { consumption_report = [], total_count = 0 } = res.data;
        setData({
          loading: "success",
          reportList: consumption_report,
          total: total_count,
        });
      })
      .catch((err) => {
        logoutErrorMessage(err, null, actions);
        setData({ ...data, loading: "error" });
      });
  }, [debouncedValue, activePage, rowsPerPage, start_date, end_date]);

  const handleSearch = (e) => setSearchValue(e.target.value);
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setActivePage(newPage + 1);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setActivePage(1);
  };

  const handleChangeDate = (e) => {
    const { name, value } = e.target;
    setDate({ ...date, [name]: value });
  };

  const closeSnackbar = () => actions.snackbarActions.hideSnackbar();
  const { openBar, type, message } = snackbars;

  // Export
  const excelObj = {
    file_name: "Monthly Consumption Report",
    url: "creport/consumption_report/export_excel",
    params: { start_date, end_date },
  };
  const { exportExcel, isLoading: isLoadingExcel } = useExcelReports(actions, excelObj);
  const { exportPdf, isLoading: isLoadingPdf } = usePdfDownload(actions, {
    file_name: "Monthly Consumption Report",
    url: "creport/consumption_report/export_pdf",
    params: { start_date, end_date },
  });

  // Calculate monthly totals
  const monthlyTotals = reportList.reduce((acc, row) => {
    headData.slice(4).forEach(col => {
      acc[col.id] = (acc[col.id] || 0) + (row[col.id] || 0);
    });
    return acc;
  }, {});

  return (
    <>
      <PageTitle title="Monthly Consumption Report" />
      <SubHeader title="Reports" subTitle="Yearly Drug Consumption by Month">
        <FontAwesomeIcon icon={faChartBar} />
      </SubHeader>

      <Container>
        <MainSnackbar variant={type} open={openBar} message={message} handleCloseBar={closeSnackbar} />
        <BackButton to="/reports/generalreports" text="Reports" />

        <div className="mb-3 text-end">
          <DataExportButton
            title="Export Report"
            exportCsv={exportExcel}
            downLoadPdf={exportPdf}
            isLoading={isLoadingExcel || isLoadingPdf}
          />
        </div>

        <DateTimeFilter
          start_date={start_date}
          end_date={end_date}
          type="date"
          handleChange={handleChangeDate}
          isFilter={false}
        />

        {/* TOTALS AT THE TOP — GOLD & BOLD */}
        {loading === "success" && reportList.length > 0 && (
          <div className="card border-0 shadow-sm mb-3" style={{ background: "linear-gradient(135deg, #1e3c72, #2a5298)" }}>
            <div className="card-body text-white">
              <h5 className="mb-3">
                <strong>TOTAL CONSUMPTION ({start_date.split("-")[0]})</strong>
              </h5>
              <div className="row text-center">
                {["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"].map((month) => (
                  <div key={month} className="col">
                    <div className="text-uppercase small opacity-75">{headData.find(h => h.id === month)?.label}</div>
                    <div className="display-6 fw-bold text-warning">
                      {formatNumber(monthlyTotals[month])}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <MuiPaper>
          <EnhancedSearchTableToolbar term={searchValue} handler={handleSearch} />

          <StickyHeadTable
            data={reportList}
            columns={headData}
            pagination={
              <CustomTablePagination
                colSpan={headData.length}
                activePage={activePage}
                page={page}
                rowsPerPage={rowsPerPage}
                count={total}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            }
          >
            {loading === "pending" && (
              <TableRow>
                <TableCell colSpan={headData.length} align="center">
                  <LoadingGif />
                </TableCell>
              </TableRow>
            )}

            {loading === "success" && reportList.length === 0 && (
              <TableRow>
                <TableCell colSpan={headData.length} align="center" style={{ fontSize: "1.1rem", color: "#7f8c8d" }}>
                  No consumption data found for the selected period
                </TableCell>
              </TableRow>
            )}

            {loading === "success" &&
              reportList.map((row, idx) => (
                <TableRow key={idx} hover>
                  <CustomTableCell>{idx + 1 + page * rowsPerPage}</CustomTableCell>
                  <CustomTableCell>
                    <strong style={{ fontSize: "1rem" }}>{row.product_name}</strong>
                  </CustomTableCell>
                  <CustomTableCell>{row.generic_name || "-"}</CustomTableCell>
                  <CustomTableCell>{row.country_of_origin || "-"}</CustomTableCell>
                  {["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"].map((month) => (
                    <CustomTableCell key={month} align="center">
                      <span
                        style={{
                          fontWeight: "bold",
                          fontSize: "1.1rem",
                          color: row[month] > 0 ? "#27ae60" : "#bdc3c7",
                        }}
                      >
                        {formatNumber(row[month])}
                      </span>
                    </CustomTableCell>
                  ))}
                </TableRow>
              ))}

            {loading === "error" && (
              <TableRow>
                <TableCell colSpan={headData.length} align="center" className="text-danger">
                  Failed to load data. Please try again.
                </TableCell>
              </TableRow>
            )}
          </StickyHeadTable>
        </MuiPaper>
      </Container>
    </>
  );
};

const mapStateToProps = (state) => ({
  snackbars: state.snackbar,
});

const mapDispatchToProps = (dispatch) => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
});

export default connect(mapStateToProps, mapDispatchToProps)(ConsumptionReport);